import { useEffect, useRef, useState } from 'react';
import { SseClient, SseEvent, SseStatus } from 'services/sse/SseClient';

export interface UseServerSentEventsOptions {
    /** Path appended to the base URL, e.g. `/events`. */
    path: string;
    onMessage: (event: SseEvent) => void;
    onError?: (error: unknown) => void;
    /** Called once per successful handshake with the raw response (e.g. to read headers). */
    onOpen?: (response: Response) => void;
    /** When false, no connection is opened. Defaults to true. */
    enabled?: boolean;
    /** Base URL override; defaults to the Go backend. */
    baseUrl?: string;
}

export interface UseServerSentEventsResult {
    status: SseStatus;
    lastEvent: SseEvent | null;
}

/**
 * React adapter around {@link SseClient}. Opens an authenticated SSE connection
 * on mount and aborts it on unmount. Event handling stays consumer-defined: the
 * `onMessage` callback decides whether to dispatch to Redux, invalidate React
 * Query, or set local state.
 */
export const useServerSentEvents = (options: UseServerSentEventsOptions): UseServerSentEventsResult => {
    const { path, onMessage, onError, onOpen, enabled = true, baseUrl } = options;
    const [status, setStatus] = useState<SseStatus>('closed');
    const [lastEvent, setLastEvent] = useState<SseEvent | null>(null);

    // Keep the latest callbacks without reopening the connection when their identity changes.
    const onMessageRef = useRef(onMessage);
    const onErrorRef = useRef(onError);
    const onOpenRef = useRef(onOpen);
    onMessageRef.current = onMessage;
    onErrorRef.current = onError;
    onOpenRef.current = onOpen;

    useEffect(() => {
        if (!enabled) return;

        const client = new SseClient({
            path,
            baseUrl,
            onMessage: (event) => {
                setLastEvent(event);
                onMessageRef.current(event);
            },
            onStatusChange: setStatus,
            onError: (error) => onErrorRef.current?.(error),
            onOpen: (response) => onOpenRef.current?.(response),
        });

        client.start();
        return () => client.stop();
    }, [path, baseUrl, enabled]);

    return { status, lastEvent };
};

export default useServerSentEvents;
